import { useCallback, useEffect, useRef, useState } from "react";
import React from "react";
import SuggestionItem from "./SuggestionItem";
import { Popover, Tooltip } from "@mui/material";
import clsx from "clsx";
import { Icon } from "components/Icon";
import Spinner from "components/Spinner";
import { copyToClipboard } from "utils/index";

interface IAutocompleteProps {
  suggestions: any[];
  className?: string;
  optionLabel: string;
  width?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  defaultSelected?: any;
  placeholder: string;
  onInputChange?: (value: string) => any;
  onSelect: (item: any, event?: any) => void;
  onClear?: (item?: any) => void;
  onSelectItem?: (item: boolean) => void;
  copyable?: boolean;
  inputTooltip?: string;
  hasLabel?: boolean;
  isOpen?: boolean;
  error?: boolean;
  required?: boolean;
  loading?: boolean;
  parentBackgroundColor?: string;
}

export const AutoComplete = ({
  suggestions,
  optionLabel,
  defaultSelected,
  placeholder,
  onInputChange,
  onSelect,
  className,
  disabled = false,
  autoFocus = true,
  copyable = false,
  onClear,
  inputTooltip,
  hasLabel = true,
  error = false,
  width = "w-[20rem]",
  isOpen,
  onSelectItem,
  required,
  loading = false,
  parentBackgroundColor = "bg-gray-white",
}: IAutocompleteProps) => {
  const [activeSuggestion, setActiveSuggestion] = useState(1000);
  const [filteredSuggestions, setFilteredSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(
    isOpen === undefined ? false : isOpen
  );
  const [userInput, setUserInput] = useState(
    defaultSelected ? defaultSelected[optionLabel] : ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const filteredSuggestionsRef = useRef<any>(null);
  /** Width */
  const [parentWidth, setParentWidth] = useState(0);
  const [spanWidth, setSpanWidth] = useState(0);
  const parentRef: any = React.useRef(null);
  const anchorElRef: any = React.useRef(null);
  const spanRef: any = React.useRef(null);
  const textSpanRef: any = React.useRef(null);

  useEffect(() => {
    if (isOpen) {
      setShowSuggestions(true);
    }
  }, [isOpen]);

  useEffect(() => {
    setAnchorEl(anchorElRef.current);
  }, [anchorElRef.current]);

  useEffect(() => {
    // Update the width values when the component mounts
    updateWidthValues();

    // Update the width values when the window is resized
    window.addEventListener("resize", updateWidthValues);

    return () => {
      // Clean up the event listener on component unmount
      window.removeEventListener("resize", updateWidthValues);
    };
  }, [userInput]);

  const updateWidthValues = () => {
    const newParentWidth = parentRef?.current?.clientWidth;
    const newSpanWidth = spanRef?.current?.scrollWidth;

    setParentWidth(newParentWidth);
    setSpanWidth(newSpanWidth);
  };
  useEffect(() => {
    if (defaultSelected !== undefined) {
      onSelect(defaultSelected);
      setUserInput(defaultSelected[optionLabel]);
    } else {
      setUserInput("");
    }
  }, [defaultSelected]);

  ///Handle click outside
  useEffect(() => {
    function handleClickOutside(event: any) {
      if (
        filteredSuggestionsRef.current &&
        !filteredSuggestionsRef.current.contains(event.target)
      ) {
        setFilteredSuggestions([]);
        setShowSuggestions(false);
        onSelectItem?.(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [filteredSuggestionsRef]);

  useEffect(() => {
    setIsLoading(true);
    if (showSuggestions && suggestions) {
      setFilteredSuggestions(suggestions);
      setIsLoading(false);
    }
  }, [showSuggestions, suggestions]);

  const onFocus = (e: any) => {
    const inputValue = e.target.value;
    if (inputValue.length === 0) {
      if (onInputChange) {
        onInputChange("");
      }
      setFilteredSuggestions(suggestions);
    }

    if (inputValue.length > 0) {
      const _filteredSuggestions = suggestions.filter((suggestion: any) =>
        suggestion[optionLabel].includes(inputValue)
      );
      setActiveSuggestion(0);
      setFilteredSuggestions(_filteredSuggestions);
    }
  };

  useEffect(() => {
    if (showSuggestions) {
      onChange("");
    }
  }, [showSuggestions]);

  const onChange = (inputValue: string) => {
    setSearchInput(inputValue);
    if (onInputChange) {
      if (inputValue !== "") setIsLoading(true);
      onInputChange(inputValue);
      setActiveSuggestion(0);
    } else {
      const _filteredSuggestions = suggestions.filter((suggestion: any) =>
        suggestion[optionLabel].includes(inputValue)
      );
      setActiveSuggestion(0);
      setFilteredSuggestions(_filteredSuggestions);
    }
  };

  const onClick = useCallback(
    (clickedSuggestion: string, e: any) => {
      setActiveSuggestion(0);
      setFilteredSuggestions([]);
      setShowSuggestions(false);
      onSelectItem?.(true);
      const _selectedItem = suggestions.find((suggestion: any) => {
        return (
          String(suggestion[optionLabel]).trim() ===
          String(clickedSuggestion).trim()
        );
      });
      onSelect(_selectedItem, e);
      setUserInput(clickedSuggestion);
    },
    [onSelect, optionLabel, suggestions]
  );

  const onKeyDown = (e: any) => {
    // User pressed the enter key
    if (e.keyCode === 13) {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
      onSelectItem?.(true);
      setUserInput(filteredSuggestions[activeSuggestion][optionLabel]);
      const _selectedItem = filteredSuggestions.find(
        (suggestion: any, index) => {
          return (
            String(suggestion[optionLabel]).trim() ===
            String(filteredSuggestions[activeSuggestion][optionLabel]).trim()
          );
        }
      );
      onSelect(_selectedItem, e);
    }
    // User pressed the up arrow
    else if (e.keyCode === 38) {
      if (activeSuggestion === 0) {
        return;
      }

      setActiveSuggestion((prev) => prev - 1);
    }
    // User pressed the down arrow
    else if (e.keyCode === 40) {
      if (activeSuggestion - 1 === filteredSuggestions.length) {
        return;
      }
      setActiveSuggestion((prev) => prev + 1);
    }
  };

  return (
    <div
      ref={parentRef}
      className={clsx(
        "border border-gray-40 rounded h-[36px] cursor-pointer",
        !disabled ? "hover:border-gray-130" : "!cursor-default",
        error && "!border-error-80",
        className
      )}
    >
      <div
        ref={anchorElRef}
        className="relative h-full flex"
        onClick={(event: any) => {
          if (!disabled) {
            setShowSuggestions(true);
            setAnchorEl(event.currentTarget);
          }
        }}
      >
        {loading && (
          <div className={"h-full flex justify-center items-center mr-2"}>
            <Spinner size={12} />
          </div>
        )}
        {(userInput?.length === 0 || !userInput) && (
          <div
            className={clsx(
              "flex pr-3 items-center text-gray-90",
              error && "!text-error-80",
              width
            )}
          >
            {`${placeholder} ${required ? "*" : ""} `}
          </div>
        )}
        {userInput?.length > 0 && (
          <div className={clsx("flex h-full", width)}>
            <div className="flex-grow">
              {hasLabel && (
                <div className="mt-[-12px]">
                  <span
                    className={clsx(
                      parentBackgroundColor,
                      "text-gray-90 text-[0.9em] mr-2 px-1",
                      error && "!text-error-80",
                      disabled && "!text-gray-70"
                    )}
                  >
                    {placeholder}
                  </span>
                </div>
              )}
              <Tooltip title={inputTooltip || userInput} placement="top">
                <div
                  className={clsx(
                    "truncate overflow-clip w-full",
                    !hasLabel ? "mt-[8px]" : "mt-[2px]"
                  )}
                >
                  <span
                    ref={textSpanRef}
                    className={clsx(
                      "truncate block mr-2 px-1 overflow-clip",
                      disabled && "!text-gray-70"
                    )}
                    style={{
                      width: `${
                        parentWidth - spanWidth - (copyable ? 40 : 20)
                      }px`,
                    }}
                  >
                    {userInput}
                  </span>
                </div>
              </Tooltip>
            </div>
            <div
              className={clsx("flex justify-end", copyable ? "w-12" : "w-6")}
            >
              {copyable && (
                <div
                  ref={spanRef}
                  className="text-xl ml-1 flex items-center justify-center cursor-pointer"
                  onClick={() => {
                    const text = textSpanRef?.current?.firstChild?.nodeValue;
                    copyToClipboard(text);
                  }}
                >
                  <Icon
                    icon="MdCopyAll"
                    className="text-gray-40 hover:text-blue-80"
                  />
                </div>
              )}
              <div
                ref={spanRef}
                className="text-xl ml-1 flex items-center justify-center cursor-pointer"
                onClick={() => {
                  if (!disabled) {
                    setUserInput("");
                    setShowSuggestions(false);
                    onSelectItem?.(false);
                    onClear?.();
                  }
                }}
              >
                <Icon
                  icon="MdClose"
                  className="text-gray-40 hover:text-gray-80"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <Popover
        open={showSuggestions}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {showSuggestions && (
          <div
            onKeyDown={onKeyDown}
            ref={filteredSuggestionsRef}
            className={clsx(
              "z-30 shadow-md rounded-md bg-gray-white border border-gray-40 p-2",
              width
            )}
          >
            <input
              type="text"
              className="flex-1 border p-2 rounded w-full mb-1 focus:border-blue-90 focus:outline-none"
              onChange={(e) => onChange(e.target.value)}
              placeholder={"اینجا بنویسید"}
              value={searchInput}
              onFocus={onFocus}
              autoFocus={autoFocus}
              disabled={disabled}
            />
            <div>
              {isLoading ? (
                <div className="w-full flex pr-2 justify-start items-center">
                  <Spinner size={18} />
                  <span className="p-2">در حال جست و جو...</span>
                </div>
              ) : filteredSuggestions.length ? (
                <ul className="suggestions  w-full bg-white overflow-y-auto max-h-96 rounded">
                  {filteredSuggestions.map((suggestion: any, index: number) => {
                    return (
                      suggestion[optionLabel] && (
                        <SuggestionItem
                          key={index}
                          isDisabled={suggestion?.isDisabled}
                          suggestion={suggestion[optionLabel]}
                          input={searchInput}
                          isHighlighted={index === activeSuggestion}
                          onClick={onClick}
                        />
                      )
                    );
                  })}
                </ul>
              ) : (
                <div className="no-suggestions bg-white z-[1000] w-full h-[34px] flex items-center">
                  <span className="p-2">موردی یافت نشد!</span>
                </div>
              )}
            </div>
          </div>
        )}
      </Popover>
    </div>
  );
};
